// Ánh xạ module → trang + sổ điểm mở rộng toàn cục. Mỗi module một thư mục pages/<id> (index.tsx + meta.ts);
// meta.ts khai báo dữ liệu thuần (FSD.md §2.1) — mọi export trừ createItems là tuỳ chọn. Gộp theo thứ tự MODULES.
// Tách gói theo route: trang module nạp khi vào route (lazyComponent — router gọi preload khi điều hướng / rê chuột lên
// liên kết); meta.ts nạp ngay vì menu "Tạo mới", Tìm nhanh, lớp phủ, thanh trên và thời gian thực cần ở mọi trang.
import type { RealtimeBinding } from '@/shared/api';
import { lazyComponent, overlayRegistry, type OverlayDef, type Preloadable } from '@/shared/lib';
import type { CreateItem, SearchProvider, TopbarItem } from '@/widgets/app-shell';
import { MODULES } from '@/entities/tenant';
import * as dashboardMeta from '@/pages/dashboard/meta';
import * as processMeta from '@/pages/process/meta';
import * as customersMeta from '@/pages/customers/meta';
import * as inventoryMeta from '@/pages/inventory/meta';
import * as bookingMeta from '@/pages/booking/meta';
import * as scheduleMeta from '@/pages/schedule/meta';
import * as dealsMeta from '@/pages/deals/meta';
import * as attendanceMeta from '@/pages/attendance/meta';
import * as salesMeta from '@/pages/sales/meta';
import * as commissionMeta from '@/pages/commission/meta';
import * as hrMeta from '@/pages/hr/meta';
import * as auditMeta from '@/pages/audit/meta';
import * as accountsMeta from '@/pages/accounts/meta';
import * as settingsMeta from '@/pages/settings/meta';

/** Hợp đồng pages/<m>/meta.ts. */
export interface ModuleMeta {
  /** Menu "Tạo mới" + nhóm "Tạo mới" của Tìm nhanh. */
  createItems: CreateItem[];
  /** Nguồn kết quả cho Tìm nhanh (Ctrl K). */
  searchProviders?: SearchProvider[];
  /** Lớp phủ mở được từ mọi trang bằng search param. */
  overlays?: OverlayDef[];
  /** Phần tử nhỏ trên thanh trên (VD pill chấm công). */
  topbarItems?: TopbarItem[];
  /** Chủ đề SSE ⇒ khoá cần làm mới. */
  realtime?: RealtimeBinding[];
}

/** Trang theo module — mỗi trang một chunk riêng. */
export const PAGES: Partial<Record<string, Preloadable>> = {
  dashboard: lazyComponent(() => import('@/pages/dashboard'), (x) => x.DashboardPage, 'DashboardPage'),
  process: lazyComponent(() => import('@/pages/process'), (x) => x.ProcessPage, 'ProcessPage'),
  customers: lazyComponent(() => import('@/pages/customers'), (x) => x.CustomersPage, 'CustomersPage'),
  inventory: lazyComponent(() => import('@/pages/inventory'), (x) => x.InventoryPage, 'InventoryPage'),
  booking: lazyComponent(() => import('@/pages/booking'), (x) => x.BookingPage, 'BookingPage'),
  schedule: lazyComponent(() => import('@/pages/schedule'), (x) => x.SchedulePage, 'SchedulePage'),
  deals: lazyComponent(() => import('@/pages/deals'), (x) => x.DealsPage, 'DealsPage'),
  attendance: lazyComponent(() => import('@/pages/attendance'), (x) => x.AttendancePage, 'AttendancePage'),
  sales: lazyComponent(() => import('@/pages/sales'), (x) => x.SalesPage, 'SalesPage'),
  commission: lazyComponent(() => import('@/pages/commission'), (x) => x.CommissionPage, 'CommissionPage'),
  hr: lazyComponent(() => import('@/pages/hr'), (x) => x.HrPage, 'HrPage'),
  audit: lazyComponent(() => import('@/pages/audit'), (x) => x.AuditPage, 'AuditPage'),
  accounts: lazyComponent(() => import('@/pages/accounts'), (x) => x.AccountsPage, 'AccountsPage'),
  settings: lazyComponent(() => import('@/pages/settings'), (x) => x.SettingsPage, 'SettingsPage'),
};

const META: Partial<Record<string, ModuleMeta>> = {
  dashboard: dashboardMeta,
  process: processMeta,
  customers: customersMeta,
  inventory: inventoryMeta,
  booking: bookingMeta,
  schedule: scheduleMeta,
  deals: dealsMeta,
  attendance: attendanceMeta,
  sales: salesMeta,
  commission: commissionMeta,
  hr: hrMeta,
  audit: auditMeta,
  accounts: accountsMeta,
  settings: settingsMeta,
};

const metas = MODULES.flatMap((m) => (META[m.id] ? [META[m.id]!] : []));

export const CREATE_ITEMS: CreateItem[] = metas.flatMap((m) => m.createItems);
export const SEARCH_PROVIDERS: SearchProvider[] = metas.flatMap((m) => m.searchProviders ?? []);
export const OVERLAYS: OverlayDef[] = metas.flatMap((m) => m.overlays ?? []);
/** Khoá lớp phủ ⇒ tham số đi kèm (useOverlay); khoá trùng giữa hai module ⇒ lỗi ngay khi nạp (dev). */
export const OVERLAY_REGISTRY = overlayRegistry(OVERLAYS);
export const TOPBAR_ITEMS: TopbarItem[] = metas.flatMap((m) => m.topbarItems ?? []);
export const REALTIME: RealtimeBinding[] = metas.flatMap((m) => m.realtime ?? []);
